import { useState } from 'react';

import { AbsoluteFill, Img, interpolate, useCurrentFrame, useVideoConfig } from 'remotion';

import {
  BASIC_MOVEMENT_BUNDLE_SCALING,
  FADE_IN_AND_OUT_EFFECT,
  IMAGE_BARS_PERCENTAGE,
  INTRO_OUTRO_DEFAULT_DURATION,
  MOVE_IMAGE_EFFECT,
  SCALE_IMAGE_EFFECT_DEFAULT,
  SCALE_IMAGE_EFFECT_ZOOM
} from '@/constants';
import { MovementBundle, OpacityLevel } from '@/enums/RemotionEnums';
import { ImageMovement } from '@/types';

type ImagePersonalAssetProps = {
  src: string;
  imageBordersEnabled: boolean;
  imageBorder?: string;
  movementBundleId: number;
  currentImageIndex: number;
  imageMovements: ImageMovement[];
  isAssetTextOverlay: boolean;
};

export const ImagePersonalAsset: React.FC<ImagePersonalAssetProps> = ({
  src,
  imageBordersEnabled,
  imageBorder,
  movementBundleId,
  currentImageIndex,
  imageMovements,
  isAssetTextOverlay
}) => {
  const { fps, durationInFrames, width, height } = useVideoConfig();
  const currentFrame = useCurrentFrame();

  const [imageDimensions, setImageDimensions] = useState<{
    imageAssetWidth: number;
    imageAssetHeight: number;
  }>();

  const getFitToVideoScale = (
    imageWidth: number,
    imageHeight: number,
    videoWidth: number,
    videoHeight: number
  ): number => {
    const videoRatio = videoWidth / videoHeight;

    if (imageWidth < videoWidth) {
      const imageRatio = imageWidth / imageHeight;
      if (imageRatio > videoRatio) {
        return videoWidth / imageWidth;
      }
      return videoHeight / imageHeight;
    }

    const imageRatio = videoWidth / (imageHeight * (videoWidth / imageWidth));
    if (imageRatio > videoRatio) {
      return videoWidth / imageWidth;
    }
    return videoHeight / (imageHeight * (videoWidth / imageWidth));
  };

  const getImageBorderSize = (): number => {
    if (!imageDimensions) {
      return 0;
    }

    const shorterSide = Math.min(
      imageDimensions.imageAssetWidth < width ? imageDimensions.imageAssetWidth : width,
      imageDimensions.imageAssetWidth < width
        ? imageDimensions.imageAssetHeight
        : imageDimensions.imageAssetHeight * (width / imageDimensions.imageAssetWidth)
    );

    return IMAGE_BARS_PERCENTAGE * shorterSide;
  };

  const baseScale = imageDimensions
    ? getFitToVideoScale(
        imageDimensions.imageAssetWidth,
        imageDimensions.imageAssetHeight,
        width,
        height
      )
    : 1;

  const parseImageMovementString = (value: string): number[] => {
    return value.split('|').map(Number);
  };

  const getInterpolation = (start: number, end: number) =>
    interpolate(currentFrame, [0, durationInFrames], [start, end]);

  const getRotationAngle = (): number => {
    if (movementBundleId === MovementBundle.LEFT_RIGHT) {
      if (currentImageIndex % 2 === 0) {
        return getInterpolation(
          parseImageMovementString(
            imageMovements[0].effects[MOVE_IMAGE_EFFECT].effectParameters
          )[0],
          parseImageMovementString(imageMovements[0].effects[MOVE_IMAGE_EFFECT].effectParameters)[1]
        );
      }
      return getInterpolation(
        parseImageMovementString(imageMovements[1].effects[MOVE_IMAGE_EFFECT].effectParameters)[0],
        parseImageMovementString(imageMovements[1].effects[MOVE_IMAGE_EFFECT].effectParameters)[1]
      );
    }

    if (movementBundleId === MovementBundle.DEFAULT) {
      switch (currentImageIndex % 6) {
        case 0:
          return getInterpolation(
            parseImageMovementString(
              imageMovements[0].effects[MOVE_IMAGE_EFFECT].effectParameters
            )[0],
            parseImageMovementString(
              imageMovements[0].effects[MOVE_IMAGE_EFFECT].effectParameters
            )[1]
          );
        case 1:
          return getInterpolation(
            parseImageMovementString(
              imageMovements[1].effects[MOVE_IMAGE_EFFECT].effectParameters
            )[0],
            parseImageMovementString(
              imageMovements[1].effects[MOVE_IMAGE_EFFECT].effectParameters
            )[1]
          );
        case 2:
          return getInterpolation(
            parseImageMovementString(
              imageMovements[2].effects[MOVE_IMAGE_EFFECT].effectParameters
            )[0],
            parseImageMovementString(
              imageMovements[2].effects[MOVE_IMAGE_EFFECT].effectParameters
            )[1]
          );
        case 3:
          return getInterpolation(
            parseImageMovementString(
              imageMovements[3].effects[MOVE_IMAGE_EFFECT].effectParameters
            )[0],
            parseImageMovementString(
              imageMovements[3].effects[MOVE_IMAGE_EFFECT].effectParameters
            )[1]
          );
        case 4:
          return getInterpolation(
            parseImageMovementString(
              imageMovements[4].effects[MOVE_IMAGE_EFFECT].effectParameters
            )[0],
            parseImageMovementString(
              imageMovements[4].effects[MOVE_IMAGE_EFFECT].effectParameters
            )[1]
          );
        case 5:
          return getInterpolation(
            parseImageMovementString(
              imageMovements[5].effects[MOVE_IMAGE_EFFECT].effectParameters
            )[0],
            parseImageMovementString(
              imageMovements[5].effects[MOVE_IMAGE_EFFECT].effectParameters
            )[1]
          );
        default:
          return 0;
      }
    }

    return 0;
  };

  const getScale = (): number => {
    if (movementBundleId === MovementBundle.DEFAULT || movementBundleId === MovementBundle.ZOOM) {
      if (currentImageIndex % 2 === 0) {
        const scale =
          getInterpolation(
            parseImageMovementString(
              imageMovements[0].effects[
                movementBundleId === MovementBundle.DEFAULT
                  ? SCALE_IMAGE_EFFECT_DEFAULT
                  : SCALE_IMAGE_EFFECT_ZOOM
              ].effectParameters
            )[0],
            parseImageMovementString(
              imageMovements[0].effects[
                movementBundleId === MovementBundle.DEFAULT
                  ? SCALE_IMAGE_EFFECT_DEFAULT
                  : SCALE_IMAGE_EFFECT_ZOOM
              ].effectParameters
            )[1]
          ) * baseScale;
        return scale;
      }
      const scale =
        getInterpolation(
          parseImageMovementString(
            imageMovements[1].effects[
              movementBundleId === MovementBundle.DEFAULT
                ? SCALE_IMAGE_EFFECT_DEFAULT
                : SCALE_IMAGE_EFFECT_ZOOM
            ].effectParameters
          )[0],
          parseImageMovementString(
            imageMovements[1].effects[
              movementBundleId === MovementBundle.DEFAULT
                ? SCALE_IMAGE_EFFECT_DEFAULT
                : SCALE_IMAGE_EFFECT_ZOOM
            ].effectParameters
          )[1]
        ) * baseScale;
      return scale;
    } else {
      return baseScale * BASIC_MOVEMENT_BUNDLE_SCALING;
    }
  };

  const getOpacity = (): number => {
    const introFrames = INTRO_OUTRO_DEFAULT_DURATION * fps;
    const outroStart = durationInFrames - introFrames;

    if (currentFrame < introFrames) {
      return interpolate(
        currentFrame,
        [0, introFrames],
        [
          parseImageMovementString(
            imageMovements[0].effects[FADE_IN_AND_OUT_EFFECT].effectParameters
          )[0],
          parseImageMovementString(
            imageMovements[0].effects[FADE_IN_AND_OUT_EFFECT].effectParameters
          )[1]
        ]
      );
    }

    if (currentFrame > outroStart) {
      return interpolate(
        currentFrame,
        [outroStart, durationInFrames],
        [
          parseImageMovementString(
            imageMovements[0].effects[FADE_IN_AND_OUT_EFFECT].effectParameters
          )[1],
          parseImageMovementString(
            imageMovements[0].effects[FADE_IN_AND_OUT_EFFECT].effectParameters
          )[0]
        ]
      );
    }

    return OpacityLevel.OPAQUE;
  };

  const scale = getScale();

  return (
    <AbsoluteFill className='pointer-events-none flex items-center justify-center'>
      {isAssetTextOverlay ? (
        <Img src={src} style={{ opacity: getOpacity() }} />
      ) : (
        <Img
          src={src}
          onLoad={(e) => {
            setImageDimensions({
              imageAssetWidth: e.currentTarget.naturalWidth,
              imageAssetHeight: e.currentTarget.naturalHeight
            });
          }}
          width={imageDimensions?.imageAssetWidth}
          height={imageDimensions?.imageAssetHeight}
          style={{
            border: imageBordersEnabled
              ? `${getImageBorderSize()}px solid ${imageBorder ?? '#fff'}`
              : undefined,
            opacity: getOpacity(),
            transform: `rotate(${getRotationAngle()}deg) scale(${scale})`
          }}
        />
      )}
    </AbsoluteFill>
  );
};
